import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./DroneMap.scss";

// Helper/sentinel to represent "no marker" without using null
// const HIDDEN_COORD = { lat: "--.------", lng: "--.------" };

// Custom hook to handle map events
const MapEvents = ({ onMouseMove, onClick }) => {
  const map = useMap();

  useEffect(() => {
    const handleMouseMove = (e) => onMouseMove(e.latlng);
    const handleClick = (e) => onClick(e.latlng);

    map.on("mousemove", handleMouseMove);
    map.on("click", handleClick);

    return () => {
      map.off("mousemove", handleMouseMove);
      map.off("click", handleClick);
    };
  }, [map, onMouseMove, onClick]);

  return null;
};

// Custom component to handle fitting bounds
const FitBoundsToMarkers = ({ markers, gpsLogs }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Only do work when Leaflet has initialized panes and is in the DOM.
    const container = map.getContainer?.();
    const pane = map.getPane?.("mapPane");
    if (!container || !container.isConnected || !pane) return;

    // Don’t run while the map is animating a zoom/pan.
    if (map._animatingZoom) return;

    const validGps = (gpsLogs || [])
      .filter((log) => log.coordinates && isValidCoordinate(log.coordinates))
      .map((log) => log.coordinates);

    const validUser = (markers || []).filter(isValidCoordinate);

    const all = [...validGps, ...validUser];
    if (all.length === 0) return;

    // Wrap in whenReady in case this is the first pass
    map.whenReady(() => {
      try {
        if (all.length === 1) {
          map.setView([all[0].lat, all[0].lng], 15, { animate: false });
        } else {
          const bounds = L.latLngBounds(all.map((c) => [c.lat, c.lng]));
          map.fitBounds(bounds, { padding: [50, 50], animate: false });
        }
      } catch (err) {
        console.error("Error fitting bounds:", err);
      }
    });

    // On unmount, stop any in-flight animation to avoid zoom transition callbacks
    return () => {
      try {
        map.stop?.();
      } catch {}
    };
  }, [map, gpsLogs, markers]); // include markers!

  return null;
};

// Helper to validate coordinates
function isValidCoordinate(coord) {
  if (!coord) return false;

  // Check if coordinates are valid numbers
  const lat =
    typeof coord.lat === "number"
      ? coord.lat
      : typeof coord.lat === "string" && coord.lat !== "--.------"
      ? parseFloat(coord.lat)
      : null;

  const lng =
    typeof coord.lng === "number"
      ? coord.lng
      : typeof coord.lng === "string" && coord.lng !== "--.------"
      ? parseFloat(coord.lng)
      : null;

  return (
    lat !== null &&
    !isNaN(lat) &&
    lng !== null &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

// Helper to calculate center from multiple markers
const getCenterFromMarkers = (markers, gpsLogs) => {
  // Default center if no valid coordinates
  const defaultCenter = [51.5485, -2.5642];

  // Get all valid coordinates
  const validMarkers = markers.filter(isValidCoordinate);

  const validGpsCoordinates = gpsLogs
    .filter((log) => log.coordinates && isValidCoordinate(log.coordinates))
    .map((log) => log.coordinates);

  const allValidCoords = [...validMarkers, ...validGpsCoordinates];

  if (allValidCoords.length === 0) return defaultCenter;

  // Calculate average center
  const latSum = allValidCoords.reduce((sum, coord) => sum + coord.lat, 0);
  const lngSum = allValidCoords.reduce((sum, coord) => sum + coord.lng, 0);

  return [latSum / allValidCoords.length, lngSum / allValidCoords.length];
};

// Helper to check if two coordinates are the same (within a small threshold)
// const isSameLocation = (coord1, coord2, threshold = 0.0001) => {
//   if (!coord1 || !coord2) return false;

//   return (
//     Math.abs(coord1.lat - coord2.lat) < threshold &&
//     Math.abs(coord1.lng - coord2.lng) < threshold
//   );
// };

const DroneMap = ({
  marker,
  setMarker,
  flag = "single",
  gpsLogs = [],
  level = "rookie",
}) => {
  const [hoverPosition, setHoverPosition] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const mapRef = useRef();

  // Sync markers from parent
  useEffect(() => {
    if (flag === "single") {
      // treat sentinel or null/undefined as "no marker"
      const isHidden =
        !marker ||
        (typeof marker.lat === "string" && marker.lat === "--.------") ||
        (typeof marker.lng === "string" && marker.lng === "--.------");

      const cleanMarker = !isHidden ? ensureValidCoordinates(marker) : null;
      setMarkers(cleanMarker ? [cleanMarker] : []);
    } else {
      const cleanMarkers = Array.isArray(marker)
        ? marker.map((m) => ensureValidCoordinates(m)).filter(Boolean)
        : [];
      setMarkers(cleanMarkers);
    }
  }, [marker, flag]);

  // Helper function to ensure coordinates are valid numbers
  function ensureValidCoordinates(marker) {
    if (!marker) return null;

    try {
      // Skip markers with hidden format
      if (
        (typeof marker.lat === "string" && marker.lat === "--.------") ||
        (typeof marker.lng === "string" && marker.lng === "--.------")
      ) {
        return null;
      }

      const lat =
        typeof marker.lat === "number"
          ? marker.lat
          : parseFloat(String(marker.lat).trim());
      const lng =
        typeof marker.lng === "number"
          ? marker.lng
          : parseFloat(String(marker.lng).trim());

      // Check if conversion resulted in valid numbers
      if (isNaN(lat) || isNaN(lng)) {
        // console.error("Invalid lat/lng values after conversion:", marker);
        return null;
      }

      // Check if in valid range
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        // console.error("Lat/lng values out of valid range:", marker);
        return null;
      }

      return { lat, lng };
    } catch (e) {
      console.error("Error processing marker:", marker, e);
      return null;
    }
  }

  // Determine center based on all valid coordinates
  const calculatedCenter = getCenterFromMarkers(markers, gpsLogs);

  // Fix default Leaflet icon paths and create smaller default icon
  delete L.Icon.Default.prototype._getIconUrl;

  const existingPinIcon = L.divIcon({
    html: `
      <div class="pulsing-circles existing-pin">
        <div class="layer-4"></div>
        <div class="layer-3"></div>
        <div class="layer-2"></div>
        <div class="layer-1"></div>
      </div>
    `,
    className: "circle-plus-icon",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  // Smaller user marker icon
  const userPinIcon = L.divIcon({
    html: `
      <div class="pulsing-circles user-pin">
        <div class="layer-4"></div>
        <div class="layer-3"></div>
        <div class="layer-2"></div>
        <div class="layer-1"></div>
      </div>
    `,
    className: "circle-plus-icon",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  const handleMouseMove = (latlng) => setHoverPosition(latlng);

  // COMMENTED OUT: Handle marker click to remove it
  // const handleMarkerClick = (clickedMarker) => {
  //   if (flag === "single") {
  //     setMarkers([]); // visually remove
  //     setMarker?.(HIDDEN_COORD); // do NOT send null
  //   } else {
  //     const newMarkers = markers.filter(
  //       (m) => !isSameLocation(m, clickedMarker)
  //     );
  //     setMarkers(newMarkers);
  //     setMarker?.(newMarkers);
  //   }
  // };

  const handleClick = (latlng) => {
    // Add new marker on map click
    if (flag === "single") {
      setMarkers([latlng]);
      setMarker(latlng);
    } else if (
      (level === "intermediate" && markers.length === 3) ||
      (level === "advanced" && markers.length === 6)
    ) {
      return; // Max markers reached for level
    } else {
      const newMarkers = [...markers, latlng];
      setMarkers(newMarkers);
      setMarker(newMarkers);
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <MapContainer
        zoom={15}
        scrollWheelZoom={true}
        zoomControl={false}
        ref={mapRef}
        center={calculatedCenter}
        className={`field-map ${level}`}
        style={{ backgroundColor: "transparent" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='© <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* FitBounds component to handle auto-zooming */}
        <FitBoundsToMarkers markers={markers} gpsLogs={gpsLogs} />

        {/* GPS Logs - Only show valid coordinates */}
        {gpsLogs.map((log, index) => {
          // Skip entries with hidden coordinates format
          if (
            log.coordinates &&
            typeof log.coordinates.lat === "string" &&
            log.coordinates.lat === "--.------"
          ) {
            return null;
          }

          // Process only valid coordinates
          if (log.coordinates && isValidCoordinate(log.coordinates)) {
            const markerId = `gps-${index}`;
            return (
              <Marker
                key={markerId}
                position={[log.coordinates.lat, log.coordinates.lng]}
                icon={existingPinIcon}
                eventHandlers={{
                  mouseover: () => setHoveredMarkerId(markerId),
                  mouseout: () => setHoveredMarkerId(null),
                }}
              >
                {hoveredMarkerId === markerId && (
                  <Tooltip direction="top" offset={[0, -16]} opacity={1.0}>
                    {log.timestamp}: lat:{" "}
                    {log.coordinates.lat.toString().substring(0, 7)}, lng:{" "}
                    {log.coordinates.lng.toString().substring(0, 7)}
                  </Tooltip>
                )}
                {/* <Popup>
                  {log.timestamp}: lat:{" "}
                  {log.coordinates.lat.toString().substring(0, 8)}, lng:{" "}
                  {log.coordinates.lng.toString().substring(0, 8)}
                </Popup> */}
              </Marker>
            );
          }

          return null;
        })}

        {/* User markers */}
        {markers.map((markerPosition, index) => {
          const markerId = `user-${index}`;
          return (
            <Marker
              key={markerId}
              position={[markerPosition.lat, markerPosition.lng]}
              icon={userPinIcon}
              eventHandlers={{
                // COMMENTED OUT: Click event that removes marker
                // click: () => handleMarkerClick(markerPosition),
                mouseover: () => setHoveredMarkerId(markerId),
                mouseout: () => setHoveredMarkerId(null),
              }}
            >
              {hoveredMarkerId === markerId && (
                <Tooltip direction="top" offset={[0, -16]} opacity={1.0}>
                  lat: {markerPosition.lat.toFixed(4)}, lng:{" "}
                  {markerPosition.lng.toFixed(4)}
                </Tooltip>
              )}
              {/* <Popup>
                lat: {markerPosition.lat.toFixed(3)}, lng:{" "}
                {markerPosition.lng.toFixed(3)}
                <br />
                <small>(Click marker to remove)</small>
              </Popup> */}
            </Marker>
          );
        })}

        <MapEvents onMouseMove={handleMouseMove} onClick={handleClick} />
      </MapContainer>

      {hoverPosition && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "5px 10px",
            borderRadius: "5px",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          lat: {hoverPosition.lat.toFixed(4)}, lng:{" "}
          {hoverPosition.lng.toFixed(4)}
        </div>
      )}
    </div>
  );
};

export default DroneMap;
